import { useState, useMemo, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { PROJECT_TYPES } from 'utils/globalVariable';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';

import { Box, Divider, Typography } from '@mui/material';
import { Stack } from '@mui/material';
import selectors from 'store/selectors';
import UsersAndTeamsIcon from 'components/Icons/sidebarIcons/usersAndTeamsIcon';
import { PreviewCnt, StyledCard } from './styled';
import LocationIcon from 'components/Icons/locationIcon';
import moment from 'moment-timezone';
import PreviewAssigneeList from './previewAssigneeList';
import {
  CREATE_SEPARATE_TASK_SITE_WISE,
  CREATE_SEPARATE_TASK_USER_WISE,
} from 'components/TaskInputs/assignTo/config/utils';
import useLocationsUtils from 'utils/CustomHooks/useLocationsUtils';
import { getTimeZone } from 'utils/globalFunction';
import { getLocationGroupsState } from 'store/selectors/locationGroups';

interface CreateTaskPropTypes {
  onClickViewWorkOrder?: (task?: any) => void;
  projectTab?: string;
  newTask?: any;
  taskTime?: { startTime: string; dueTime: string };
}

const monthMap: Record<string, string> = {
  '1 Month': 'month',
  '2 Month': '2 months',
  '3 Month': '3 months',
  '6 Month': '6 months',
  '9 Month': '9 months',
  '12 Month': 'annually',
};

const weekMap: Record<string, string> = {
  Week1: 'week',
  Weeks2: '2 weeks',
  Weeks3: '3 weeks',
  Weeks4: '4 weeks',
  Weeks5: '5 weeks',
  Weeks6: '6 weeks',
};

const daysMap: Record<string, string> = {
  Mon: 'Monday',
  Tue: 'Tuesday',
  Wed: 'Wednesday',
  Thu: 'Thursday',
  Fri: 'Friday',
  Sat: 'Saturday',
  Sun: 'Sunday',
};

function ProjectPreview(props: CreateTaskPropTypes) {
  const { projectTab, newTask, taskTime } = props;
  const tz: string = getTimeZone();
  const { getLocationSitesOnly } = useLocationsUtils();

  const roles = useSelector(selectors.getRoles);
  const authorizedUsers = useSelector(selectors.getAuthorizedUsers);
  const locationsList = useSelector(selectors.getLocationsState);
  const locationGroups = useSelector(getLocationGroupsState);

  const [view, setView] = useState<'preview' | 'location' | 'assignee'>(
    'preview',
  );

  const {
    intervalWeek,
    recurringByEvery,
    scheduledDateTime,
    endDate,
    requestThisTask,
    recurringOnDay,
    assignees,
    assigneesRoles,
    startTime,
    dueTime,
    title = 'Untitled Project',
  } = newTask || {};

  const goToPreview = useCallback(() => setView('preview'), []);
  const goToAssignee = useCallback(() => setView('assignee'), []);

  const formatTextHelper = useCallback(
    (frequencyDesc: string) => {
      let description = frequencyDesc;

      if (scheduledDateTime) {
        const formattedDate = moment(scheduledDateTime)
          .tz(tz)
          .format('MMM D, YYYY');
        description += `, starting from ${formattedDate}`;
      } else {
        description += `, starting from today`;
      }

      if (endDate && endDate !== scheduledDateTime) {
        const formattedDate = moment(endDate).tz(tz).format('MMM D, YYYY');
        description += `, ${
          requestThisTask === 'Monthly' ? 'until' : 'till'
        } ${formattedDate}`;
      }

      return description;
    },
    [scheduledDateTime, endDate, requestThisTask],
  );

  const flattSiteLocations = useMemo(() => {
    if (!locationsList) return [];

    const siteLocations: any[] = [];

    const extractSiteLocations = (locations: any[]) => {
      if (!locations || !Array.isArray(locations)) return;

      locations.forEach((location) => {
        if (location.Level?.isSite) {
          siteLocations.push(location.id);
        }

        if (location.Sublocations && Array.isArray(location.Sublocations)) {
          extractSiteLocations(location.Sublocations);
        }
      });
    };

    extractSiteLocations(locationsList);
    return siteLocations;
  }, [locationsList]);

  const scheduleFrequecyText = useMemo(() => {
    if (!requestThisTask) return '';

    // Helper functions inside the memo to avoid recreating them
    const dailyText = () => formatTextHelper('Repeat Daily');

    const weeklyText = () => {
      const daysOfWeek = !intervalWeek
        ? ''
        : intervalWeek.length === 7
        ? 'everyday'
        : intervalWeek.map((d: string) => daysMap[d]).join(', ');
      return formatTextHelper(
        `Repeat every ${weekMap[recurringByEvery] || ''} on ${daysOfWeek}`,
      );
    };

    const monthlyText = () => {
      const month = monthMap[recurringByEvery] || '';
      const prefix =
        recurringByEvery === '12 months' ? 'Repeat' : 'Repeat every';
      return formatTextHelper(
        `${prefix} ${month} on the ${recurringOnDay} day of the month`,
      );
    };

    if (requestThisTask === 'daily') return dailyText();
    if (requestThisTask === 'Weekly') {
      if (intervalWeek?.length === 7 && recurringByEvery === 'Week1') {
        return dailyText();
      }
      return weeklyText();
    }
    if (requestThisTask === 'Monthly') return monthlyText();

    return '';
  }, [
    requestThisTask,
    recurringByEvery,
    intervalWeek,
    recurringOnDay,
    formatTextHelper,
  ]);

  const assigneeArray = useMemo(() => {
    if (!newTask) return [];

    if (projectTab === PROJECT_TYPES.roleBasedProject) {
      const selectedRole = roles?.hashRoles?.[assigneesRoles?.roles?.[0]] || {};
      const roleAssignees =
        selectedRole?.HotelUsers?.map((user) => user.UserId) || [];

      if (assigneesRoles?.locations?.length > 0) {
        const siteLocations = getLocationSitesOnly(assigneesRoles?.locations);
        return (
          authorizedUsers
            ?.filter((user) => {
              const result =
                roleAssignees?.includes(user.id) &&
                siteLocations.some((locationId) =>
                  user.UserLocations?.some(
                    (location) => location.LocationId === locationId,
                  ),
                );
              return result;
            })
            .map((user) => user.id) || []
        );
      } else {
        return (
          authorizedUsers
            ?.filter((user) => {
              const result =
                roleAssignees?.includes(user.id) &&
                flattSiteLocations.some((locationId) =>
                  user.UserLocations?.some(
                    (location) => location.LocationId === locationId,
                  ),
                );
              return result;
            })
            .map((user) => user.id) || []
        );
      }
    }

    return assignees || [];
  }, [
    assignees,
    assigneesRoles,
    authorizedUsers,
    newTask,
    projectTab,
    roles?.hashRoles,
  ]);

  const locationsArray = useMemo(() => {
    if (!newTask) return [];

    if (projectTab === PROJECT_TYPES.roleBasedProject) {
      if (assigneesRoles?.roles) {
        if (assigneesRoles?.locationGroups?.length) {
          // Get locations from each group ID using the locationGroups data
          const allLocations: string[] = [];
          assigneesRoles.locationGroups.forEach((groupId) => {
            const group = locationGroups?.find((g) => g.id === groupId);
            if (group?.LocationGroupLocations?.length) {
              const groupLocationIds = group.LocationGroupLocations.map(
                (location) => location.LocationId,
              );
              allLocations.push(...groupLocationIds);
            }
          });
          return getLocationSitesOnly(allLocations);
        }

        return assigneesRoles?.locations?.length > 0
          ? getLocationSitesOnly(assigneesRoles.locations)
          : flattSiteLocations;
      }
      return [];
    }

    return [
      ...new Set(
        authorizedUsers
          .filter((user) => assignees?.includes(user.id))
          .map((i) => i.defaultLocationId),
      ),
    ];
  }, [
    assignees,
    assigneesRoles,
    authorizedUsers,
    flattSiteLocations,
    locationGroups,
    newTask,
    projectTab,
  ]);

  const separateSchedulesCount = useMemo(() => {
    if (projectTab === PROJECT_TYPES.userBasedProject) {
      return assignees?.length || 0;
    }
    if (projectTab === PROJECT_TYPES.assetBasedProject) {
      return newTask?.assets?.length || 0;
    }
    if (projectTab === PROJECT_TYPES.roleBasedProject) {
      return assigneesRoles?.creationType === CREATE_SEPARATE_TASK_SITE_WISE
        ? locationsArray?.length || 0
        : assigneeArray?.length || 0;
    }
    return 0;
  }, [
    projectTab,
    assignees,
    newTask?.assets,
    assigneesRoles,
    locationsArray,
    assigneeArray,
  ]);

  const startTimeText = useMemo(() => {
    if (taskTime?.startTime)
      return moment(taskTime.startTime, 'HH:mm A').format('hh:mm A');
    if (startTime) return moment(startTime, 'HH:mm A').format('hh:mm A');
    return '-';
  }, [taskTime]);

  const dueTimeText = useMemo(() => {
    if (taskTime?.dueTime)
      return moment(taskTime.dueTime, 'HH:mm A').format('hh:mm A');
    if (dueTime) return moment(dueTime, 'HH:mm A').format('hh:mm A');
    return '-';
  }, [taskTime]);
  return (
    <PreviewCnt>
      <Typography className="title">
        {view !== 'preview' && <ArrowBackRoundedIcon onClick={goToPreview} />}
        {view === 'preview'
          ? 'Project Preview'
          : view === 'location'
          ? 'Locations'
          : 'Assignees'}
      </Typography>

      {view === 'preview' && (
        <>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            gap={'12px'}
            mb={'12px'}
          >
            <StyledCard>
              <div>
                <Typography className="cardValue">
                  {locationsArray?.length || '-'}
                </Typography>
                <Typography className="cardTitle">Location</Typography>
              </div>

              <Box
                className="icon"
                sx={{ background: '#E1F5FE', '& path': { fill: '#03A9F4' } }}
              >
                <LocationIcon filled />
              </Box>
            </StyledCard>
            <StyledCard
              sx={{
                cursor: 'pointer',
                '&:hover': {
                  border: '1px solid #6868FE',
                },
              }}
              onClick={goToAssignee}
            >
              <div>
                <Typography className="cardValue">
                  {assigneeArray?.length || '-'}
                </Typography>
                <Typography className="cardTitle">Assignees</Typography>
              </div>

              <Box
                className="icon"
                sx={{ background: '#E0F2F1', '& path': { fill: '#009688' } }}
              >
                <UsersAndTeamsIcon filled />
              </Box>
            </StyledCard>
          </Stack>
          <StyledCard mb={'12px'}>
            <Box flex={1}>
              <Typography className="cardTitle">Frequency</Typography>
              <Typography className="cardText">
                {scheduleFrequecyText}
              </Typography>
              <Divider sx={{ my: 2 }} />
              <Typography className="cardTitle">Schedules</Typography>
              <Typography className="cardText">
                {separateSchedulesCount > 0 ? (
                  <>
                    <b>{separateSchedulesCount} separate schedule(s)</b> will be
                    grouped into project "{title}"
                  </>
                ) : (
                  '-'
                )}
              </Typography>
            </Box>
          </StyledCard>
          <StyledCard>
            <Box flex={1}>
              <Typography className="cardTitle">Start Time</Typography>
              <Typography className="cardText">{startTimeText}</Typography>
            </Box>
            <Box
              sx={{
                borderLeft: '1px solid #E0E0E0',
                height: '40px',
                mx: 2,
              }}
            />
            <Box flex={1}>
              <Typography className="cardTitle">Due Time</Typography>
              <Typography className="cardText">{dueTimeText}</Typography>
            </Box>
          </StyledCard>
        </>
      )}
      {view === 'location' && (
        <StyledCard>
          <Box flex={1}>
            <Typography className="cardTitle">Location</Typography>
            <Typography className="cardText">
              {newTask?.locations?.length || '-'}
            </Typography>
          </Box>
        </StyledCard>
      )}
      {view === 'assignee' && <PreviewAssigneeList assignees={assigneeArray} />}
    </PreviewCnt>
  );
}

export default ProjectPreview;
